// VaultifyUK collection page enhancements.
// Loaded defer from main-collection-product-grid.liquid.

(function () {

  // ── Drawer trigger wiring ─────────────────────────────────────────────────
  // The toolbar filter button and mobile sticky bar buttons all open the same
  // menu-drawer by programmatically clicking its <summary> element.

  function getDrawerSummary() {
    return document.querySelector('.mobile-facets__open-wrapper');
  }

  function openDrawer() {
    const summary = getDrawerSummary();
    if (summary) summary.click();
  }

  function initDrawerTriggers() {
    const filterBtn = document.getElementById('ToolbarFilterBtn');
    const stickyFilter = document.getElementById('StickyFilterBtn');
    const stickySort = document.getElementById('StickySortBtn');

    if (filterBtn) filterBtn.addEventListener('click', openDrawer);
    if (stickyFilter) stickyFilter.addEventListener('click', openDrawer);

    if (stickySort) {
      stickySort.addEventListener('click', () => {
        openDrawer();
        // After drawer opens, focus the sort select for direct access
        setTimeout(() => {
          const mobileSort = document.getElementById('SortBy-mobile');
          if (mobileSort) mobileSort.focus();
        }, 300);
      });
    }
  }

  // ── Drawer open/close watchers ────────────────────────────────────────────
  // Two independent concerns (toolbar aria-expanded, sticky bar visibility)
  // both need to react to the same drawer's open attribute toggling. Rather
  // than each creating its own MutationObserver on the identical target with
  // identical options, they share one observer via this small registry.

  let drawerOpenObserver = null;
  const drawerOpenCallbacks = [];

  function watchDrawerOpenState(callback) {
    const drawer = document.querySelector('.mobile-facets__disclosure');
    if (!drawer) return;
    drawerOpenCallbacks.push(callback);
    if (!drawerOpenObserver) {
      drawerOpenObserver = new MutationObserver(() => {
        drawerOpenCallbacks.forEach((cb) => cb(drawer));
      });
      drawerOpenObserver.observe(drawer, { attributes: true, attributeFilter: ['open'] });
    }
  }

  // ── aria-expanded on toolbar filter button ────────────────────────────────
  // Reflects the drawer open/close state on the toolbar button.

  function initDrawerAriaExpanded() {
    const filterBtn = document.getElementById('ToolbarFilterBtn');
    const drawer = document.querySelector('.mobile-facets__disclosure');
    if (!filterBtn || !drawer) return;

    watchDrawerOpenState((d) => {
      filterBtn.setAttribute('aria-expanded', d.hasAttribute('open') ? 'true' : 'false');
    });
  }

  // ── Filter count badge ────────────────────────────────────────────────────
  // Counts active facet-remove chips in .active-facets-desktop and updates
  // both the toolbar badge and the mobile sticky bar badge.

  function initFilterBadge() {
    const toolbarBadge = document.querySelector('.collection-toolbar__filter-count');
    const stickyBadge = document.querySelector('.collection-sticky-bar__count');
    const activeFacets = document.querySelector('.active-facets-desktop');

    if (!activeFacets) return;

    function update() {
      // Count chips, excluding the Clear All wrapper
      const count = activeFacets.querySelectorAll('facet-remove:not(.active-facets__button-wrapper)').length;
      [toolbarBadge, stickyBadge].forEach((badge) => {
        if (!badge) return;
        badge.textContent = count;
        badge.classList.toggle('hidden', count === 0);
      });
      // Sync drawer header after AJAX fetch (immediate update handled by initDrawerLiveCount)
      updateDrawerActiveTotal(count);
      // Update aria-label on toolbar filter button
      const filterBtn = document.getElementById('ToolbarFilterBtn');
      if (filterBtn) {
        const base = filterBtn.querySelector('.collection-toolbar__filter-label');
        const label = base ? base.textContent.trim() : 'Filter';
        filterBtn.setAttribute(
          'aria-label',
          count > 0 ? `${label}, ${count} active` : label
        );
      }
      // Sync the price filter's "highest price" label with the rendered grid
      updatePriceRangeMax();
    }

    update();
    // renderActiveFacets replaces .active-facets-desktop innerHTML on every fetch
    new MutationObserver(update).observe(activeFacets, { childList: true });
  }

  // ── Custom sort dropdown ──────────────────────────────────────────────────
  // The native <select> is visually hidden and used only for form submission.
  // The custom button+list dropdown provides full visual control. Bound to
  // both the desktop toolbar (#ToolbarSortDropdown) and the mobile drawer
  // (#MobileSortDropdown). selectOption() updates BOTH instances' displays
  // directly rather than relying on initSortSync's 'change'-based listener —
  // programmatic value changes here only dispatch 'input' (see below), which
  // that listener doesn't catch, so without this the two dropdowns would
  // drift out of sync whenever one triggered the other only via event.

  // Keyed by wrapperId so re-init of one dropdown never touches the other's
  // listener. See the outside-click removal below for why this exists.
  const sortDropdownOutsideClickHandlers = {};

  function initCustomSortDropdown(wrapperId) {
    const wrapper = document.getElementById(wrapperId);
    if (!wrapper || wrapper.dataset.vaultifySortBound) return;
    wrapper.dataset.vaultifySortBound = '1';

    // Each AJAX filter/sort re-render replaces this wrapper's parent
    // innerHTML, producing a brand-new element (the dataset guard above only
    // protects the *current* node, not re-binding across replacements — see
    // the comment at the MutationObserver call site below). Without this,
    // every re-render left the previous instance's outside-click listener on
    // document permanently — each individually harmless once its closed-over
    // wrapper is detached (wrapper.contains() is always false), but
    // accumulating indefinitely over a long filtering/sorting session.
    if (sortDropdownOutsideClickHandlers[wrapperId]) {
      document.removeEventListener('click', sortDropdownOutsideClickHandlers[wrapperId], { capture: true });
      delete sortDropdownOutsideClickHandlers[wrapperId];
    }

    const btn = wrapper.querySelector('.collection-toolbar__sort-btn');
    const list = wrapper.querySelector('.collection-toolbar__sort-list');
    const hiddenSelect = wrapper.querySelector('select[name="sort_by"]');
    if (!btn || !list || !hiddenSelect) return;

    function getOptions() {
      return Array.from(list.querySelectorAll('.collection-toolbar__sort-option'));
    }

    // open() always moves real DOM focus onto an option (the selected one, or
    // the first option when none is selected) — this is the "focus moves into
    // the listbox" pattern from the WAI-ARIA listbox authoring practice, used
    // instead of aria-activedescendant so every screen reader gets correct
    // option announcements with no extra bookkeeping.
    function open() {
      wrapper.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');

      // Flip the list upward if there isn't enough room below in the
      // viewport but there is above — mainly for the mobile drawer, where
      // the sort control often sits near the bottom of the screen.
      wrapper.classList.remove('collection-toolbar__sort-custom--flip-up');
      const btnRect = btn.getBoundingClientRect();
      const listRect = list.getBoundingClientRect();
      const spaceBelow = window.innerHeight - btnRect.bottom;
      const spaceAbove = btnRect.top;
      if (spaceBelow < listRect.height + 8 && spaceAbove > spaceBelow) {
        wrapper.classList.add('collection-toolbar__sort-custom--flip-up');
      }

      const options = getOptions();
      const focusTarget = options.find((o) => o.getAttribute('aria-selected') === 'true') || options[0];
      if (focusTarget) focusTarget.focus();
    }

    // restoreFocus: false when closing because focus is already moving
    // elsewhere (click outside, tabbing away) — calling btn.focus() in that
    // case would yank focus back and break Tab/click-outside behaviour.
    function close(restoreFocus) {
      // Guards against re-entrant calls: hiding the list via the class
      // removal below forces an immediate blur of whichever option
      // currently has focus, which fires the focusout handler further
      // down — without this guard that would call close() a second time
      // for every close() invocation.
      if (!wrapper.classList.contains('is-open')) return;
      wrapper.classList.remove('is-open');
      btn.setAttribute('aria-expanded', 'false');
      if (restoreFocus) btn.focus();
    }

    // Expose so initSortSync and the other instance's selectOption() can
    // update this dropdown's displayed label/aria-selected state.
    wrapper._updateDisplay = (value) => {
      const opt = list.querySelector(`[data-value="${CSS.escape(value)}"]`);
      const current = btn.querySelector('.collection-toolbar__sort-current');
      if (opt && current) current.textContent = opt.textContent.trim();
      list.querySelectorAll('.collection-toolbar__sort-option').forEach((o) => {
        const sel = o.dataset.value === value;
        o.setAttribute('aria-selected', sel);
        o.classList.toggle('is-selected', sel);
      });
    };

    function selectOption(value, restoreFocus) {
      hiddenSelect.value = value;
      wrapper._updateDisplay(value);
      close(restoreFocus);

      // Keep the other sort dropdown instance (desktop or mobile, whichever
      // didn't trigger this) in sync too.
      ['ToolbarSortDropdown', 'MobileSortDropdown'].forEach((otherId) => {
        if (otherId === wrapperId) return;
        const otherWrapper = document.getElementById(otherId);
        if (!otherWrapper) return;
        const otherSelect = otherWrapper.querySelector('select[name="sort_by"]');
        if (otherSelect) otherSelect.value = value;
        if (otherWrapper._updateDisplay) otherWrapper._updateDisplay(value);
      });

      // Dispatch from the mobile filter form so Dawn serialises the full form
      // (sort_by + all active filter checkboxes). Dispatching from the toolbar
      // select uses the desktop branch of onSubmitHandler which omits
      // FacetFiltersFormMobile and clears any active filters.
      const mobileSel = document.getElementById('SortBy-mobile');
      if (mobileSel) {
        mobileSel.value = value;
        mobileSel.dispatchEvent(new Event('input', { bubbles: true }));
      } else {
        hiddenSelect.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      wrapper.classList.contains('is-open') ? close(true) : open();
    });

    // Enter/Space/ArrowDown/ArrowUp on the closed trigger open the listbox
    // and move focus straight to an option, per the APG listbox button
    // pattern — a screen reader user shouldn't need a separate "open" step
    // followed by a second navigation step.
    btn.addEventListener('keydown', (e) => {
      if (['Enter', ' ', 'Spacebar', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
        e.preventDefault();
        if (!wrapper.classList.contains('is-open')) open();
      }
    });

    list.addEventListener('click', (e) => {
      const opt = e.target.closest('.collection-toolbar__sort-option');
      if (opt) selectOption(opt.dataset.value, true);
    });

    // Keyboard model for the open listbox. Scoped to this list (not a
    // document-level listener) since the handler only ever needs to react
    // while focus is already inside this specific widget.
    list.addEventListener('keydown', (e) => {
      const options = getOptions();
      const currentIndex = options.indexOf(document.activeElement);

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          options[Math.min(currentIndex + 1, options.length - 1)]?.focus();
          break;
        case 'ArrowUp':
          e.preventDefault();
          options[Math.max(currentIndex - 1, 0)]?.focus();
          break;
        case 'Home':
          e.preventDefault();
          options[0]?.focus();
          break;
        case 'End':
          e.preventDefault();
          options[options.length - 1]?.focus();
          break;
        case 'Enter':
        case ' ':
        case 'Spacebar':
          e.preventDefault();
          if (currentIndex > -1) selectOption(options[currentIndex].dataset.value, true);
          break;
        case 'Escape':
          e.preventDefault();
          close(true);
          break;
        // Tab is intentionally left to its default behaviour: every option
        // sits at tabindex="-1" (focused only programmatically via the calls
        // above), so Tab moves on to the next real tabbable element in the
        // page rather than cycling through options — the focusout handler
        // below then closes the list visually as focus leaves it.
      }
    });

    // Closes the list when focus leaves the widget entirely — covers Tab,
    // Shift+Tab, and any other way focus could move on without a click.
    // Component-scoped (fires only for this wrapper), not a document listener.
    wrapper.addEventListener('focusout', (e) => {
      if (!wrapper.contains(e.relatedTarget)) close(false);
    });

    const onOutsideClick = (e) => {
      if (!wrapper.contains(e.target)) close(false);
    };
    document.addEventListener('click', onOutsideClick, { capture: true });
    sortDropdownOutsideClickHandlers[wrapperId] = onOutsideClick;
  }

  // ── Sort select synchronisation ───────────────────────────────────────────
  // The toolbar sort (#SortByToolbar) and the mobile drawer sort (#SortBy-mobile)
  // must stay in sync so that filter submissions via the mobile form preserve
  // the sort order selected via the toolbar, and vice versa.
  //
  // renderAdditionalElements replaces .sorting innerHTML after each fetch,
  // which creates a new <select> inside #FacetSortToolbarForm. We observe
  // .sorting for childList changes to re-apply the sync listener and update
  // the mobile select value.

  function initSortSync() {
    const sortingWrapper = document.querySelector('.sorting');
    if (!sortingWrapper) return;

    function getToolbarSelect() {
      return sortingWrapper.querySelector('select[name="sort_by"]');
    }

    function syncMobileFromToolbar() {
      const toolbarSel = getToolbarSelect();
      const mobileSel = document.getElementById('SortBy-mobile');
      if (toolbarSel && mobileSel && mobileSel.value !== toolbarSel.value) {
        mobileSel.value = toolbarSel.value;
      }
    }

    function syncToolbarFromMobile() {
      const toolbarSel = getToolbarSelect();
      if (toolbarSel && this.value !== toolbarSel.value) {
        toolbarSel.value = this.value;
        // Update the custom dropdown display to match
        const dropdownWrapper = document.getElementById('ToolbarSortDropdown');
        if (dropdownWrapper && dropdownWrapper._updateDisplay) {
          dropdownWrapper._updateDisplay(this.value);
        }
      }
    }

    function bindMobileListener() {
      const mobileSel = document.getElementById('SortBy-mobile');
      if (mobileSel && !mobileSel.dataset.vaultifySyncBound) {
        mobileSel.dataset.vaultifySyncBound = '1';
        mobileSel.addEventListener('change', syncToolbarFromMobile);
      }
    }

    // Watch for .sorting innerHTML replacement (renderAdditionalElements)
    new MutationObserver(() => {
      // Re-init custom dropdown after innerHTML is replaced (bound flag is on
      // the old element which is now gone, so the new one starts fresh)
      initCustomSortDropdown('ToolbarSortDropdown');
      syncMobileFromToolbar();
    }).observe(sortingWrapper, { childList: true, subtree: true });

    // The mobile sort control lives inside a .js-filter element
    // (#Details-Mobile-SortBy-{{ section.id }}), so facets.js's renderFilters
    // replaces its innerHTML on every filter change too, same as any other
    // filter group — not just after a sort change. Re-init on every
    // replacement so #MobileSortDropdown stays bound.
    const mobileSortDetails = document.querySelector('[id^="Details-Mobile-SortBy-"]');
    if (mobileSortDetails) {
      new MutationObserver(() => {
        initCustomSortDropdown('MobileSortDropdown');
        bindMobileListener();
        syncMobileFromToolbar();
      }).observe(mobileSortDetails, { childList: true, subtree: true });
    }

    bindMobileListener();
    syncMobileFromToolbar();
  }

  // ── Drawer header live active count ──────────────────────────────────────
  // Updates the "N active" label in the drawer header immediately on checkbox
  // change — before the AJAX section fetch responds. The MutationObserver in
  // initFilterBadge handles the post-fetch sync so both stay in agreement.

  function updateDrawerActiveTotal(count) {
    const el = document.querySelector('.mobile-facets__active-total');
    if (!el) return;
    if (count > 0) {
      el.textContent = `${count} active`;
      el.classList.remove('hidden');
    } else {
      el.textContent = '';
      el.classList.add('hidden');
    }
  }

  function initDrawerLiveCount() {
    const form = document.getElementById('FacetFiltersFormMobile');
    if (!form) return;

    form.addEventListener('change', () => {
      const checked = form.querySelectorAll('input[type="checkbox"]:checked').length;
      const priceActive = Array.from(
        form.querySelectorAll('.facets__price input')
      ).some((i) => i.value.trim() !== '') ? 1 : 0;
      updateDrawerActiveTotal(checked + priceActive);
    });
  }

  // ── Sticky bar visibility ─────────────────────────────────────────────────
  // Hide the sticky bar while the drawer is open to prevent visual overlap.
  // Uses CSS :has() as primary mechanism; this JS provides a fallback.

  function initStickyBarVisibility() {
    const bar = document.getElementById('CollectionStickyBar');
    const drawer = document.querySelector('.mobile-facets__disclosure');
    if (!bar || !drawer) return;

    watchDrawerOpenState((d) => {
      bar.classList.toggle('collection-sticky-bar--hidden', d.hasAttribute('open'));
    });
  }

  // ── Show more / Show fewer ────────────────────────────────────────────────
  // Filter groups with > 6 options truncate at 6. The "Show N more" button
  // reveals all overflow items; clicking again collapses back to 6.

  function initShowMore() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.mobile-facets__show-more');
      if (!btn) return;

      const targetId = btn.dataset.facetShowMore;
      const submenu = document.getElementById('FacetMobile-' + targetId);
      if (!submenu) return;

      const isExpanded = btn.dataset.expanded === 'true';
      const overflowItems = submenu.querySelectorAll('.mobile-facets__item--overflow');

      if (isExpanded) {
        overflowItems.forEach((item) => item.classList.remove('is-visible'));
        btn.textContent = btn.dataset.moreLabel;
        btn.dataset.expanded = 'false';
      } else {
        overflowItems.forEach((item) => item.classList.add('is-visible'));
        btn.textContent = btn.dataset.lessLabel;
        btn.dataset.expanded = 'true';
      }
    });
  }

  // ── Group select-all / clear ─────────────────────────────────────────────
  // Delegated click handler for the "Select all" and "Clear" buttons
  // rendered at the top of each list filter group submenu.

  function updateGroupUI(details) {
    const checkboxes = Array.from(details.querySelectorAll('.mobile-facets__checkbox'));
    const checkedCount = checkboxes.filter((cb) => cb.checked).length;
    const anyChecked = checkedCount > 0;

    // Select all / Clear / separator visibility
    const groupActions = details.querySelector('.mobile-facets__group-actions');
    if (groupActions) {
      const uncheckable = checkboxes.filter((cb) => !cb.checked && !cb.disabled).length;
      const showSelectAll = uncheckable > 1;
      groupActions.querySelector('[data-action="select-all"]')?.classList.toggle('hidden', !showSelectAll);
      groupActions.querySelector('[data-action="clear"]')?.classList.toggle('hidden', !anyChecked);
      groupActions.querySelector('.mobile-facets__group-action-sep')?.classList.toggle('hidden', !(showSelectAll && anyChecked));
    }

    // Gold active-count badge in the summary row
    const innerDiv = details.querySelector('.mobile-facets__summary > div');
    if (innerDiv) {
      let badge = innerDiv.querySelector('.mobile-facets__active-count');
      if (anyChecked) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'mobile-facets__active-count';
          badge.setAttribute('aria-hidden', 'true');
          const caret = innerDiv.querySelector('.mobile-facets__caret');
          innerDiv.insertBefore(badge, caret);
        }
        badge.textContent = checkedCount;
      } else {
        badge?.remove();
      }
    }
  }

  function initGroupSelectClear() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.mobile-facets__group-action');
      if (!btn) return;

      const details = document.getElementById(btn.dataset.detailsId);
      if (!details) return;

      const checkboxes = Array.from(details.querySelectorAll('.mobile-facets__checkbox'));
      if (!checkboxes.length) return;

      // group-actions is outside <summary> so this click never reaches the
      // <summary> activation handler — no toggle, no flash.
      if (btn.dataset.action === 'select-all') {
        checkboxes.forEach((cb) => { if (!cb.disabled) cb.checked = true; });
      } else {
        checkboxes.forEach((cb) => { cb.checked = false; });
      }

      updateGroupUI(details);

      // Trigger Dawn's AJAX form submission via the first checkbox.
      // FacetFiltersForm (facets.js) listens for 'input' on the form, not
      // 'change' — a real user click fires both natively, but setting
      // .checked programmatically above fires neither, so 'input' must be
      // dispatched explicitly or Dawn's listener never sees this at all.
      checkboxes[0].dispatchEvent(new Event('input', { bubbles: true }));
    });

    // Keep UI in sync when individual checkboxes are toggled
    document.addEventListener('change', (e) => {
      if (!e.target.classList.contains('mobile-facets__checkbox')) return;
      const details = e.target.closest('.mobile-facets__details');
      if (details) updateGroupUI(details);
    });
  }

  // ── Price range "highest price" label ────────────────────────────────────
  // Shopify's filter.range_max returns the store-wide maximum regardless of
  // other active filters, so the server-rendered label never updates. After
  // each AJAX re-render we scan the product grid for the actual highest price
  // among the currently displayed items and update the label to match.

  function updatePriceRangeMax() {
    const grid = document.getElementById('ProductGridContainer');
    if (!grid) return;

    // card-product.liquid emits data-price-max="{{ card_product.price_max }}" on every
    // card wrapper. price_max is Shopify's internal integer (pence), so divide by 100.
    let maxCents = 0;
    grid.querySelectorAll('[data-price-max]').forEach((el) => {
      const val = parseInt(el.dataset.priceMax, 10);
      if (!isNaN(val) && val > maxCents) maxCents = val;
    });

    if (maxCents <= 0) return;

    const infoEl = document.querySelector('.facets-container-drawer .mobile-facets__info');
    if (!infoEl) return;

    const symbol = document.querySelector('.facets-container-drawer .field-currency')?.textContent?.trim() || '£';
    const pounds = maxCents / 100;
    const formatted = pounds.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    // Replace only the price token inside the existing i18n string, preserving
    // surrounding text ("The highest price is") regardless of locale.
    const updated = infoEl.textContent.replace(/[£€$¥₹][0-9,.]+/, `${symbol}${formatted}`);
    if (updated !== infoEl.textContent) infoEl.textContent = updated;
  }

  // ── Init ──────────────────────────────────────────────────────────────────

  function init() {
    initDrawerTriggers();
    initDrawerAriaExpanded();
    initFilterBadge();
    initDrawerLiveCount();
    initCustomSortDropdown('ToolbarSortDropdown');
    initCustomSortDropdown('MobileSortDropdown');
    initSortSync();
    initStickyBarVisibility();
    initShowMore();
    initGroupSelectClear();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

})();
